import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { parseThreshold } from '../lib/overview'
import type { BudgetAffordances } from '../lib/permissions'
import type { CostBuildingRef, CostRow, CostSettings } from '../types'

const CURRENCIES = ['CZK', 'EUR', 'USD', 'GBP', 'PLN']
const NONE = '__none__'

interface Props {
  open: boolean
  onClose: () => void
  settings: CostSettings
  levelOne: CostRow[]
  buildings: CostBuildingRef[]
  affordances: BudgetAffordances
  onSaveCurrency: (currency: string) => Promise<boolean>
  onSaveThreshold: (value: number | null) => Promise<boolean>
  onLinkBuilding: (nodeId: string, buildingId: string | null) => Promise<boolean>
}

const SettingsDialog: React.FC<Props> = ({ open, onClose, settings, levelOne, buildings, affordances, onSaveCurrency, onSaveThreshold, onLinkBuilding }) => {
  const { t } = useTranslation()
  const [currency, setCurrency] = useState(settings.currency)
  const [savingCurrency, setSavingCurrency] = useState(false)
  const [linking, setLinking] = useState<string | null>(null)
  useEffect(() => { if (open) setCurrency(settings.currency) }, [open, settings.currency])
  const options = CURRENCIES.includes(settings.currency) ? CURRENCIES : [settings.currency, ...CURRENCIES]

  const thresholdText = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v))
  const [threshold, setThreshold] = useState(thresholdText(settings.alertThresholdPercent))
  const [savingThreshold, setSavingThreshold] = useState(false)
  useEffect(() => { if (open) setThreshold(thresholdText(settings.alertThresholdPercent)) }, [open, settings.alertThresholdPercent])
  const parsedThreshold = parseThreshold(threshold)
  const thresholdChanged = parsedThreshold.ok && parsedThreshold.value !== (settings.alertThresholdPercent ?? null)

  const save = async (setSaving: (saving: boolean) => void, write: () => Promise<boolean>) => {
    setSaving(true)
    const ok = await write()
    setSaving(false)
    if (ok) toastr.success('', t('CostControl_settings_saved'))
  }
  const saveCurrency = () => save(setSavingCurrency, () => onSaveCurrency(currency))
  const saveThreshold = () => (parsedThreshold.ok ? save(setSavingThreshold, () => onSaveThreshold(parsedThreshold.value)) : undefined)
  const link = async (nodeId: string, value: string) => {
    setLinking(nodeId)
    await onLinkBuilding(nodeId, value === NONE ? null : value)
    setLinking(null)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose() }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader><DialogTitle>{t('CostControl_settings_title')}</DialogTitle></DialogHeader>
        <div className="min-w-0 space-y-6">
          <section className="space-y-2">
            <Label>{t('CostControl_currency')}</Label>
            <div className="flex items-center gap-2">
              <Select value={currency} onValueChange={setCurrency} disabled={!affordances.changeCurrency || savingCurrency}>
                <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
                <SelectContent>{options.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              {affordances.changeCurrency && (
                <Button size="sm" onClick={() => { void saveCurrency() }} disabled={savingCurrency || currency === settings.currency}>
                  {savingCurrency && <LoaderCircle className="h-4 w-4 animate-spin" />}
                  {t('CostControl_currency_save')}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{t('CostControl_currency_hint')}</p>
          </section>
          <section className="space-y-2">
            <Label htmlFor="cost-alert-threshold">{t('CostControl_alert_threshold')}</Label>
            <div className="flex items-center gap-2">
              <Input
                id="cost-alert-threshold"
                inputMode="numeric"
                className="h-9 w-32"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                disabled={!affordances.changeThreshold || savingThreshold}
              />
              {affordances.changeThreshold && (
                <Button size="sm" onClick={() => { void saveThreshold() }} disabled={savingThreshold || !thresholdChanged}>
                  {savingThreshold && <LoaderCircle className="h-4 w-4 animate-spin" />}
                  {t('CostControl_alert_threshold_save')}
                </Button>
              )}
            </div>
            {!parsedThreshold.ok && <p className="text-xs text-destructive">{t('CostControl_alert_threshold_invalid')}</p>}
            <p className="text-xs text-muted-foreground">{t('CostControl_alert_threshold_hint')}</p>
          </section>
          <section className="space-y-2">
            <Label>{t('CostControl_building_links')}</Label>
            <p className="text-xs text-muted-foreground">{t('CostControl_building_links_hint')}</p>
            {buildings.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('CostControl_no_buildings')}</p>
            ) : (
              <div className="divide-y rounded-md border">
                {levelOne.map((n) => (
                  <div key={n.id} className="flex items-center gap-3 px-3 py-2">
                    <span className="min-w-0 flex-1 truncate text-sm">{n.name}</span>
                    {linking === n.id && <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />}
                    <Select value={n.buildingId ?? NONE} onValueChange={(v) => { void link(n.id, v) }} disabled={!affordances.linkBuilding || linking !== null}>
                      <SelectTrigger className="h-8 w-48 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>{t('CostControl_no_building')}</SelectItem>
                        {buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>{t('Common_close')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default SettingsDialog
